/**
 * The template's grade, editable in the editor.
 *
 * A ROW field (`AssessmentTemplatePayload.grade`) that the store mirrors into `definition.grade` —
 * `setGrade` owns that mirroring, so this hook is a one-line delegation like every other editor
 * widget (B9). The create modal picks a grade once and until now nothing could correct it; it also
 * drives the standards catalogue, whose grade filter is hard (B12.5.24).
 *
 * Changing it NEVER touches tagged standards (B12.5.25) — already-tagged codes stay as authored,
 * off-grade or not. `refTaggedStandardsCount` is what any future caution would be built from.
 *
 * `null` stays reachable in the store ("no template loaded") but is not offerable here: the select
 * has no clear button, because a cleared grade blocks `getPayload` and would read as a dead Save.
 */
import { computed } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Grade } from './enums'

import { useAssessmentEditorStore } from './store'
import { gradeOptions } from './assessment-template-options'

interface UseGradeSelectReturn {
  /** Writable for `v-model` on `AppSelect`. Reads the store, writes through `setGrade`. */
  refGrade: WritableComputedRef<Grade | undefined>
  gradeSelectOptions: AppSelectOption<Grade>[]
  /** How many standards the document has tagged, across every activity. A code tagged on two
   *  activities counts twice — the caution is about how much stays behind, not distinct codes. */
  refTaggedStandardsCount: ComputedRef<number>
}

export const useGradeSelect = (): UseGradeSelectReturn => {
  const store = useAssessmentEditorStore()

  const refGrade = computed<Grade | undefined>({
    // `undefined`, not `null`: `AppSelect`'s model is `TValue | undefined`, and its placeholder
    // shows on `undefined`.
    get: () => store.getGrade ?? undefined,
    set: (value) => {
      // The select cannot clear, but the model still admits `undefined`; ignoring it keeps the
      // store from being walked back into its "not loaded" state by a UI event.
      if (value) {
        store.setGrade(value)
      }
    },
  })

  const refTaggedStandardsCount = computed<number>(() =>
    store.getScreens.reduce(
      (total, screen) => total + screen.activities.reduce(
        (screenTotal, activity) => screenTotal + (activity.standards?.length ?? 0),
        0,
      ),
      0,
    ),
  )

  return {
    refGrade,
    gradeSelectOptions: gradeOptions,
    refTaggedStandardsCount,
  }
}
